import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "pull-left ml-5" }
const _hoisted_2 = { class: "status pull-left" }
const _hoisted_3 = {
  key: 0,
  class: "text-success"
}
const _hoisted_4 = {
  key: 1,
  class: "text-warning"
}
const _hoisted_5 = {
  key: 2,
  class: "text-error"
}

import { useStore } from 'vuex';
import { 
  ref, 
  onMounted, 
  onBeforeUnmount, 
  watch,
  PropType,
} from 'vue';

import Socket, {
  EVENT_CONNECTED,
  EVENT_CONNECTING,
  EVENT_DISCONNECTED,
  EVENT_MESSAGE,
  EVENT_CONNECT_ERROR,
} from '@shell/utils/socket';
import { allHash } from '@shell/utils/promise';
import { addParams } from '@shell/utils/url';
import { base64Decode, base64Encode } from '@shell/utils/crypto';
import { useApplicationSocketMixin } from './ApplicationSocketMixin';

import Select from '@shell/components/form/Select';
import Window from '@shell/components/nav/WindowManager/Window';


export default /*@__PURE__*/_defineComponent({
  __name: 'ApplicationShell',
  props: {
  application: {
    type: Object as PropType<object>,
    default: null,
  },
  endpoint: {
    type: String as PropType<string>,
    default: '',
  },
  initialInstance: {
    type: String,
    default: null,
  },
},
  setup(__props) {

const store = useStore();
const t = store.getters['i18n/t'];

const props = __props;

const {
  socket,
  isOpen,
  backlog,
  instanceChoices,
  getRootSocketUrl,
} = useApplicationSocketMixin(props);

const active = ref<boolean>(true);
const xterm = ref<HTMLElement | null>(null);
const instance = ref<string>(props.initialInstance || instanceChoices.value[0]);
const terminal = ref<object | null>(null);
const fitAddon = ref<object | null>(null);
const searchAddon = ref<object | null>(null);
const webglAddon = ref<object | null>(null);
const isOpening = ref<boolean>(false);
const keepAliveTimer = ref<object | null>(null);
const xtermConfig = {
  allowProposedApi: true,
  cursorBlink:      true,
  useStyle:         true,
  fontSize:         12,
};

watch(
  () => instance.value,
  () => {
    connect();
  }
);

onBeforeUnmount(() => {
  clearInterval(keepAliveTimer.value);
  cleanup();
});

onMounted(async () => {
  await setupTerminal();
  await connect();

  isOpen.value = true;

  clearInterval(keepAliveTimer.value);
  keepAliveTimer.value = setInterval(() => {
    fit();
  }, 60 * 1000);
}); 

const setupTerminal = async () => {
  const docStyle = getComputedStyle(document.querySelector('body'));
  const xtermLib = await import('xterm');

  const addons = await allHash({
    fit:      import('xterm-addon-fit'),
    webgl:    import('xterm-addon-webgl'),
    weblinks: import('xterm-addon-web-links'),
    search:   import('xterm-addon-search'),
  });

  const terminalTemp = new xtermLib.Terminal({
    theme: {
      background: docStyle.getPropertyValue('--terminal-bg').trim(),
      foreground: docStyle.getPropertyValue('--terminal-text').trim(),
      cursor: docStyle.getPropertyValue('--terminal-cursor').trim(),
      selectionBackground: docStyle.getPropertyValue('--terminal-selection').trim(),
    },
    ...xtermConfig,
  });

  fitAddon.value = new addons.fit.FitAddon();
  searchAddon.value = new addons.search.SearchAddon();

  try {
    webglAddon.value = new addons.webgl.WebGlAddon();
  } catch (e: any) { // eslint-disable-line @typescript-eslint/no-unused-vars
    // Some browsers (Safari) don't support the webgl renderer, so don't use it.
    webglAddon.value = null;
  }

  terminalTemp.loadAddon(fitAddon.value);
  terminalTemp.loadAddon(searchAddon.value);
  terminalTemp.loadAddon(new addons.weblinks.WebLinksAddon());
  if (webglAddon.value) {
    terminalTemp.loadAddon(webglAddon.value);
  }
  
  terminalTemp.open(xterm.value);

  fit();
  flush();

  terminalTemp.onData((input) => {
    const msg = `0${ base64Encode(input) }`;

    write(msg);
  });

  terminal.value = terminalTemp;
};

const write = (msg) => {
  if (isOpen.value) {
    socket.value.send(msg);
  } else {
    backlog.value.push(msg);
  }
};

const clear = () => {
  terminal.value.clear();
};

const getSocketUrl = async () => {
  const { url, token } = await getRootSocketUrl();

  return addParams(url, {
    authtoken: token,
    instance: instance.value,
  });
};

const connect = async () => {
  if (socket.value) {
    await socket.value.disconnect();
    socket.value = null;
    terminal.value.reset();
  }

  const url = await getSocketUrl();

  if (!url) {
    return;
  }

  socket.value = new Socket(url, false, 0, 'base64.channel.k8s.io');

  socket.value.addEventListener(EVENT_CONNECTING, () => {
    isOpen.value = false;
    isOpening.value = true;
  });

  socket.value.addEventListener(EVENT_CONNECT_ERROR, (e) => {
    isOpen.value = false;
    isOpening.value = false;
    console.error('Connect Error', e);
  });

  socket.value.addEventListener(EVENT_CONNECTED, () => {
    isOpen.value = true;
    isOpening.value = false;
    fit();
    flush();
  });

  socket.value.addEventListener(EVENT_DISCONNECTED, () => {
    isOpen.value = false;
    isOpening.value = false;
  });

  socket.value.addEventListener(EVENT_MESSAGE, (e) => {
    const type = e.detail.data.substr(0, 1);
    const msg = base64Decode(e.detail.data.substr(1));

    if (`${ type }` === '1') {
      terminal.value.write(msg);
    } else {
      console.error(msg);
    }
  });

  socket.value.connect();
  terminal.value.focus();
};

const flush = () => {
  const backlogTemp = backlog.value.slice();

  backlog.value = [];

  for (const data of backlogTemp) {
    socket.value.send(data);
  }
};

const fit = () => {
  if (!fitAddon.value) {
    return;
  }
  
  fitAddon.value.fit();
  const { rows, cols } = fitAddon.value.proposeDimensions();

  if (!isOpen.value) {
    return;
  }

  const message = `4${ base64Encode(
    JSON.stringify({
      Width:  Math.floor(cols),
      Height: Math.floor(rows),
    })
  ) }`;

  socket.value.send(message);
};

const cleanup = () => {
  if (socket.value) {
    socket.value.disconnect();
    socket.value = null;
  }

  if (terminal.value) {
    terminal.value.dispose();
    terminal.value = null;
  }
};

return (_ctx: any,_cache: any) => {
  const _component_resize_observer = _resolveComponent("resize-observer")!
  const _directive_clean_html = _resolveDirective("clean-html")!

  return (_openBlock(), _createBlock(_unref(Window), {
    active: active.value,
    "before-close": cleanup,
    class: "epinio-app-shell"
  }, {
    title: _withCtx(() => [
      (_unref(instanceChoices).length > 1)
        ? (_openBlock(), _createBlock(_unref(Select), {
            key: 0,
            value: instance.value,
            "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((instance).value = $event)),
            disabled: _unref(instanceChoices).length === 1,
            class: "containerPicker auto-width pull-left",
            options: _unref(instanceChoices),
            clearable: false,
            placement: "top"
          }, null, 8, ["value", "disabled", "options"]))
        : _createCommentVNode("", true),
      _cache[1] || (_cache[1] = _createTextVNode()),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("button", {
          class: "btn btn-sm bg-primary",
          onClick: clear
        }, _toDisplayString(_unref(t)('wm.containerShell.clear')), 1)
      ]),
      _cache[2] || (_cache[2] = _createTextVNode()),
      _createElementVNode("div", _hoisted_2, [
        (_unref(isOpen))
          ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_unref(t)('wm.connection.connected')), 1))
          : (isOpening.value)
            ? _withDirectives((_openBlock(), _createElementBlock("span", _hoisted_4, null, 512)), [
                [_directive_clean_html, _unref(t)('wm.connection.connecting')]
              ])
            : (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(_unref(t)('wm.connection.disconnected')), 1))
      ])
    ]),
    body: _withCtx(() => [
      _createElementVNode("div", {
        class: _normalizeClass(["shell-container", { open: _unref(isOpen), closed: !_unref(isOpen) }])
      }, [
        _createElementVNode("div", {
          ref_key: "xterm",
          ref: xterm,
          class: "shell-body"
        }, null, 512),
        _cache[3] || (_cache[3] = _createTextVNode()),
        _createVNode(_component_resize_observer, { onNotify: fit })
      ], 2)
    ]),
    _: 1,
    __: [4]
  }, 8, ["active"]))
}
}

})